import React, { useState } from 'react';

// These MUST match the spec values in MapTracerPage.js CONDUIT_SPECS
const DEFAULT_CONDUIT_COLORS = {
  '1x1.25':  '#00B050',
  '1x1.25f': '#FF00FF',
  '2x1.25':  '#00B050',
  '2x1.25f': '#FF00FF',
  '3x1.25f': '#996633',
  '4x1.25f': '#663311',
  '5x1.25f': '#660000',
  '1x2.00':  '#0000FF',
  '2x2.00':  '#00CCFF',
  '3x2.00':  '#66DDDD',
  'combo1':  '#00B050',
  'combo2':  '#800000',
  'combo3':  '#8B0000',
  'combo4':  '#800080',
};

// These MUST match the spec values in MapTracerPage.js FIBER_SPECS
const DEFAULT_FIBER_COLORS = {
  'co432':  '#FF6600',
  'bfo432': '#993300',
  'co288':  '#FF0000',
  'bfo288': '#CC0000',
  'bfo48m': '#00CC00',
  'bfo24m': '#008000',
  'bfo48':  '#003399',
  'bfo24':  '#0000FF',
  'co48':   '#0066CC',
  'co24':   '#6633CC',
};

const DEFAULT_DEVICE_COLORS = {
  handhole: '#2196F3', flowerpot: '#FF9800', splitter_1x4: '#4CAF50',
  splitter_2x8: '#E91E63', splice_point: '#9C27B0', slack_loop: '#00BCD4',
  ground_rod: '#795548',
};

export function getDefaultStyles() {
  return {
    conduitColors: { ...DEFAULT_CONDUIT_COLORS },
    fiberColors: { ...DEFAULT_FIBER_COLORS },
    deviceColors: { ...DEFAULT_DEVICE_COLORS },
    conduitWeight: 1.0,
    fiberWeight: 1.0,
    fiberDashed: false,
  };
}

export default function StyleSettingsPanel({ styleOverrides, setStyleOverrides, onClose, darkMode }) {
  const [activeTab, setActiveTab] = useState('conduit');

  const bg = darkMode ? '#1a1a2e' : '#ffffff';
  const text = darkMode ? '#e0e0e0' : '#333333';
  const border = darkMode ? '#333' : '#ddd';
  const tabBg = darkMode ? '#16213e' : '#f5f5f5';
  const activeBg = darkMode ? '#0f3460' : '#e3f2fd';

  const updateColor = (category, key, color) => {
    setStyleOverrides(prev => ({
      ...prev,
      [category]: { ...prev[category], [key]: color },
    }));
  };

  const updateWeight = (key, value) => {
    setStyleOverrides(prev => ({ ...prev, [key]: value }));
  };

  const resetDefaults = () => {
    setStyleOverrides(getDefaultStyles());
  };

  const ColorGrid = ({ colors, category }) => (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8 }}>
      {Object.entries(colors).map(([key, defaultColor]) => (
        <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <input
            type="color"
            value={styleOverrides[category]?.[key] || defaultColor}
            onChange={e => updateColor(category, key, e.target.value)}
            style={{ width: 28, height: 28, border: 'none', cursor: 'pointer', borderRadius: 4 }}
          />
          <span style={{ fontSize: 11, color: text, fontFamily: 'monospace' }}>{key}</span>
        </div>
      ))}
    </div>
  );

  const tabs = [
    { id: 'conduit', label: 'Conduit' },
    { id: 'fiber', label: 'Fiber' },
    { id: 'device', label: 'Devices' },
    { id: 'lines', label: 'Lines' },
  ];

  return (
    <div style={{
      position: 'absolute', top: 70, right: 10, width: 360, maxHeight: 'calc(100vh - 100px)',
      background: bg, border: `1px solid ${border}`, borderRadius: 8,
      boxShadow: '0 4px 20px rgba(0,0,0,0.3)', zIndex: 1000, overflow: 'hidden',
      display: 'flex', flexDirection: 'column',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', borderBottom: `1px solid ${border}` }}>
        <span style={{ fontWeight: 'bold', color: text, fontSize: 14 }}>Style Settings</span>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: text, cursor: 'pointer', fontSize: 18 }}>×</button>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: `1px solid ${border}` }}>
        {tabs.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
            flex: 1, padding: '8px 4px', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600,
            background: activeTab === tab.id ? activeBg : tabBg,
            color: activeTab === tab.id ? '#2196F3' : text,
            borderBottom: activeTab === tab.id ? '2px solid #2196F3' : '2px solid transparent',
          }}>{tab.label}</button>
        ))}
      </div>

      {/* Content */}
      <div style={{ padding: 14, overflowY: 'auto', flex: 1 }}>
        {activeTab === 'conduit' && <ColorGrid colors={DEFAULT_CONDUIT_COLORS} category="conduitColors" />}
        {activeTab === 'fiber' && <ColorGrid colors={DEFAULT_FIBER_COLORS} category="fiberColors" />}
        {activeTab === 'device' && <ColorGrid colors={DEFAULT_DEVICE_COLORS} category="deviceColors" />}
        {activeTab === 'lines' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <label style={{ fontSize: 12, color: text, fontWeight: 600 }}>Conduit Weight: {(styleOverrides.conduitWeight || 1).toFixed(1)}x</label>
              <input type="range" min="0.5" max="3" step="0.1" value={styleOverrides.conduitWeight || 1}
                onChange={e => updateWeight('conduitWeight', parseFloat(e.target.value))}
                style={{ width: '100%' }} />
            </div>
            <div>
              <label style={{ fontSize: 12, color: text, fontWeight: 600 }}>Fiber Weight: {(styleOverrides.fiberWeight || 1).toFixed(1)}x</label>
              <input type="range" min="0.5" max="3" step="0.1" value={styleOverrides.fiberWeight || 1}
                onChange={e => updateWeight('fiberWeight', parseFloat(e.target.value))}
                style={{ width: '100%' }} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" checked={styleOverrides.fiberDashed || false}
                onChange={e => updateWeight('fiberDashed', e.target.checked)} />
              <label style={{ fontSize: 12, color: text }}>Dashed fiber lines</label>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={{ padding: '10px 14px', borderTop: `1px solid ${border}`, display: 'flex', justifyContent: 'flex-end' }}>
        <button onClick={resetDefaults} style={{
          padding: '6px 14px', borderRadius: 4, border: `1px solid ${border}`,
          background: 'transparent', color: text, cursor: 'pointer', fontSize: 12,
        }}>Reset to Defaults</button>
      </div>
    </div>
  );
}
